import { createDirect } from '../api/client';
import type { ChatSummary } from '../api/types';
import { startDirectCall } from '../calls/startDirectCall';

export type CallMode = 'audio' | 'video';

/**
 * Параметры экрана звонка из сводки личного чата. `remoteUserId` обязателен по смыслу: без него
 * экран исходящего не знает, чьё лицо показать, и рисует заглушку.
 */
export function callParamsFromChat(chat: ChatSummary, peerUserId: string, mode: CallMode, fallbackTitle?: string) {
  return {
    chatId: chat.id,
    title: chat.title || fallbackTitle || '',
    incoming: false,
    callMode: mode,
    callScope: 'direct' as const,
    remoteUserId: peerUserId,
    remoteHasAvatar: Boolean(chat.peerHasAvatar),
    remoteAvatarRevision:
      typeof chat.peerAvatarRevision === 'number' && Number.isFinite(chat.peerAvatarRevision)
        ? chat.peerAvatarRevision
        : 0,
    remoteAvatarShape: chat.peerAvatarShape ?? null,
  };
}

/**
 * Звонок из уже известного личного чата (строка списка): без запроса к серверу. Через общий
 * вход `startDirectCall`: он глотает двойное нажатие и возвращает в идущий звонок, а не
 * начинает второй поверх свёрнутого.
 */
export function startCallFromChat(chat: ChatSummary, peerUserId: string, mode: CallMode): void {
  void startDirectCall(callParamsFromChat(chat, peerUserId, mode));
}

/**
 * Звонок человеку (карточка контакта, «Новое сообщение»): сначала получить или завести личный
 * чат — звонок адресуется чату, — затем открыть экран звонка. Ошибку отдаёт вызывающему: только
 * он знает, как её показать.
 */
export async function startCallWithUser(peer: { userId: string; displayName: string }, mode: CallMode): Promise<void> {
  await startDirectCall(async () => {
    const chat = await createDirect(peer.userId);
    return callParamsFromChat(chat, chat.peerUserId ?? peer.userId, mode, peer.displayName);
  });
}
